import {
    maxScrollLeft,
    setScrollEdgeMask,
    clearScrollEdgeMask
} from './horizontal-scroll-edge.js';
import { markRenderArtifact } from './render-artifact.js';

const INNER_CLASS = 'truncated-hover-scroll__inner';
    const VIEWPORT_CLASS = 'truncated-hover-scroll__viewport';
    const ROOT_CLASS = 'truncated-hover-scroll';
    const ACTIVE_CLASS = 'truncated-hover-scroll--active';
    const GRID_LISTENERS_ATTR = 'data-grid-truncated-scroll-bound';
    const SCROLL_END_PADDING_PX = 16;
    const SCROLL_SPEED_PX_PER_SEC = 52;
    const MIN_SCROLL_DURATION_MS = 1400;
    const MAX_SCROLL_DURATION_MS = 10000;

    const TRUNCATED_CELL_SELECTOR = [
        '.data-grid-header .data-grid-cell:not(.data-grid-cell-check) .sortable-label',
        '.data-grid-item-visible > .data-grid-row .data-grid-cell:not(.data-grid-cell-check)'
    ].join(', ');

    const scrollAnimationByCell = new WeakMap();
    const resizeObserverByGrid = new WeakMap();
    const resizeTimeoutByGrid = new WeakMap();
    const cellSelectorByRoot = new WeakMap();

    function cellSelectorFor(root) {
        const selector = cellSelectorByRoot.get(root);
        if (!selector) {
            throw new Error('truncated-text-hover-scroll: cell selector is required');
        }
        return selector;
    }

    const RESIZE_SETTLE_DELAY_MS = 150;

    function viewportHasOverflow(viewport) {
        return maxScrollLeft(viewport) > 1;
    }

    function findTruncatedCell(eventTarget, grid) {
        if (!eventTarget || !grid) {
            return null;
        }
        const cell = eventTarget.closest(cellSelectorFor(grid));
        if (!cell || !grid.contains(cell) || cell.closest('[data-grid-no-truncated-scroll]')) {
            return null;
        }
        return cell;
    }

    function findScrollWrapper(cell) {
        const viewport = cell.querySelector(':scope > .' + VIEWPORT_CLASS);
        if (!viewport) {
            return null;
        }
        const inner = viewport.querySelector(':scope > .' + INNER_CLASS);
        if (!inner) {
            return null;
        }
        return { cell, viewport, inner };
    }

    function cellTextIsTruncated(cell) {
        const wrapper = findScrollWrapper(cell);
        if (wrapper) {
            return viewportHasOverflow(wrapper.viewport);
        }
        return cell.scrollWidth > cell.clientWidth + 1;
    }

    function createScrollWrapper(cell, knownTruncated) {
        const existing = findScrollWrapper(cell);
        if (existing) {
            return existing;
        }
        const truncated = typeof knownTruncated === 'boolean' ? knownTruncated : cellTextIsTruncated(cell);
        if (!truncated) {
            return null;
        }

        const viewport = markRenderArtifact(document.createElement('div'));
        viewport.className = VIEWPORT_CLASS;
        const inner = markRenderArtifact(document.createElement('span'));
        inner.className = INNER_CLASS;
        viewport.appendChild(inner);
        cell.appendChild(viewport);
        while (cell.firstChild !== viewport) {
            inner.appendChild(cell.firstChild);
        }
        cell.classList.add(ROOT_CLASS);
        return { cell, viewport, inner };
    }

    function stopScrollAnimation(cell) {
        const animation = scrollAnimationByCell.get(cell);
        if (!animation) {
            return;
        }
        if (animation.frameId) {
            cancelAnimationFrame(animation.frameId);
        }
        scrollAnimationByCell.delete(cell);
    }

    function resetHoverScroll(cell) {
        stopScrollAnimation(cell);
        cell.classList.remove(ACTIVE_CLASS);
        const wrapper = findScrollWrapper(cell);
        if (!wrapper) {
            return;
        }
        wrapper.viewport.scrollLeft = 0;
        wrapper.inner.style.paddingRight = '';
        clearScrollEdgeMask(wrapper.viewport);
    }

    function removeScrollWrapper(cell) {
        stopScrollAnimation(cell);
        cell.classList.remove(ACTIVE_CLASS, ROOT_CLASS);
        const wrapper = findScrollWrapper(cell);
        if (!wrapper) {
            return;
        }
        while (wrapper.inner.firstChild) {
            cell.appendChild(wrapper.inner.firstChild);
        }
        wrapper.inner.remove();
        wrapper.viewport.remove();
    }

    function removeScrollWrapperIfNeeded(cell) {
        if (cell.classList.contains(ROOT_CLASS)) {
            removeScrollWrapper(cell);
        }
    }

    function updateCellScrollWrapper(cell, selector) {
        if (!cell.matches(selector)) {
            removeScrollWrapperIfNeeded(cell);
            return;
        }
        if (!cellTextIsTruncated(cell)) {
            removeScrollWrapperIfNeeded(cell);
            return;
        }
        createScrollWrapper(cell);
    }

    function refreshGridCells(grid) {
        if (!grid) {
            return;
        }

        const selector = cellSelectorFor(grid);
        const candidateCells = Array.from(grid.querySelectorAll(selector));
        const rootCells = Array.from(grid.querySelectorAll('.' + ROOT_CLASS));

        // Read phase: measure every cell before mutating any of them, so layout
        // is only forced to recalculate once instead of once per cell.
        const truncatedByCell = candidateCells.map((cell) => [cell, cellTextIsTruncated(cell)]);

        // Write phase: apply create/remove using the measurements above, with no
        // further layout-forcing reads interleaved between mutations.
        truncatedByCell.forEach(([cell, truncated]) => {
            if (truncated) {
                createScrollWrapper(cell, true);
            } else {
                removeScrollWrapperIfNeeded(cell);
            }
        });
        rootCells.forEach((cell) => {
            if (!cell.matches(selector)) {
                removeScrollWrapper(cell);
            }
        });
    }

    function scrollCellToEndOnHover(cell, viewport, inner) {
        inner.style.paddingRight = SCROLL_END_PADDING_PX + 'px';
        const maxScroll = maxScrollLeft(viewport);
        if (maxScroll <= 1) {
            inner.style.paddingRight = '';
            clearScrollEdgeMask(viewport);
            return;
        }

        cell.classList.add(ACTIVE_CLASS);
        setScrollEdgeMask(viewport, viewport);

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const durationMs = prefersReducedMotion
            ? 0
            : Math.max(
                MIN_SCROLL_DURATION_MS,
                Math.min(MAX_SCROLL_DURATION_MS, (maxScroll / SCROLL_SPEED_PX_PER_SEC) * 1000)
            );

        if (durationMs === 0) {
            viewport.scrollLeft = maxScroll;
            setScrollEdgeMask(viewport, viewport);
            return;
        }

        const startScrollLeft = viewport.scrollLeft;
        const startedAt = performance.now();
        const animation = { frameId: 0 };
        scrollAnimationByCell.set(cell, animation);

        const tick = (now) => {
            const progress = Math.min(1, (now - startedAt) / durationMs);
            const eased = progress < 0.5
                ? 2 * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            viewport.scrollLeft = startScrollLeft + (maxScroll - startScrollLeft) * eased;
            setScrollEdgeMask(viewport, viewport);
            if (progress < 1) {
                animation.frameId = requestAnimationFrame(tick);
            } else {
                scrollAnimationByCell.delete(cell);
            }
        };
        animation.frameId = requestAnimationFrame(tick);
    }

    function startHoverScroll(cell, grid) {
        updateCellScrollWrapper(cell, cellSelectorFor(grid));
        const wrapper = findScrollWrapper(cell);
        if (!wrapper) {
            return;
        }
        requestAnimationFrame(() => {
            if (!viewportHasOverflow(wrapper.viewport)) {
                removeScrollWrapper(cell);
                return;
            }
            stopScrollAnimation(cell);
            scrollCellToEndOnHover(cell, wrapper.viewport, wrapper.inner);
        });
    }

    function refreshGridOnNextFrame(grid) {
        requestAnimationFrame(() => refreshGridCells(grid));
    }

    function attachGridListeners(grid) {
        if (!grid || grid.getAttribute(GRID_LISTENERS_ATTR) === '1') {
            return;
        }
        grid.setAttribute(GRID_LISTENERS_ATTR, '1');

        grid.addEventListener('data-grid-rendered', () => refreshGridOnNextFrame(grid));
        grid.addEventListener('data-grid-layout-changed', () => refreshGridOnNextFrame(grid));

        grid.addEventListener('mouseover', (event) => {
            const cell = findTruncatedCell(event.target, grid);
            if (!cell || cell.contains(event.relatedTarget)) {
                return;
            }
            startHoverScroll(cell, grid);
        });

        grid.addEventListener('mouseout', (event) => {
            const cell = findTruncatedCell(event.target, grid);
            if (!cell || cell.contains(event.relatedTarget)) {
                return;
            }
            resetHoverScroll(cell);
        });
    }

    function watchGridResize(grid) {
        if (!grid || typeof ResizeObserver === 'undefined' || resizeObserverByGrid.has(grid)) {
            return;
        }
        const observer = new ResizeObserver(() => {
            clearTimeout(resizeTimeoutByGrid.get(grid));
            resizeTimeoutByGrid.set(grid, setTimeout(() => refreshGridCells(grid), RESIZE_SETTLE_DELAY_MS));
        });
        observer.observe(grid);
        resizeObserverByGrid.set(grid, observer);
    }

function attachToGrid(grid, cellSelector) {
    if (!grid) {
        return;
    }
    const selector = cellSelector || TRUNCATED_CELL_SELECTOR;
    if (!selector) {
        throw new Error('attachToGrid: cellSelector is required');
    }
    cellSelectorByRoot.set(grid, selector);
    attachGridListeners(grid);
    watchGridResize(grid);
    refreshGridCells(grid);
}

export { attachToGrid, refreshGridCells as refreshGrid };
